import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Paper,
  Grid,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import api from "../services/api";

const Dashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    waitingReply: 0,
    closed: 0,
  });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    priority: "medium",
    category: "general",
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await api.get("/tickets");
      if (response.data.success) {
        const ticketsData = response.data.tickets;

        // Sort tickets: Waiting Reply -> Open -> In Progress -> Resolved -> Closed
        const sortedTickets = ticketsData.sort((a, b) => {
          // Define status priority
          const statusPriority = {
            "waiting-reply": 1,
            open: 2,
            "in-progress": 3,
            resolved: 4,
            closed: 5,
          };

          const aPriority = statusPriority[a.status] || 999;
          const bPriority = statusPriority[b.status] || 999;

          // First sort by status priority
          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }

          // Within same status, sort by creation date (newest first)
          return new Date(b.createdAt) - new Date(a.createdAt);
        });

        setTickets(sortedTickets);

        // Stats calculation depends on user role
        if (user?.role === "user") {
          // For regular users:
          // - "Open" includes both "open" and "waiting-reply" statuses
          // - "Reply Received" (返答あり) shows "in-progress" status
          // - "Waiting Reply" is not displayed
          setStats({
            total: ticketsData.length,
            open: ticketsData.filter(
              (t) => t.status === "open" || t.status === "waiting-reply"
            ).length,
            inProgress: ticketsData.filter((t) => t.status === "in-progress")
              .length,
            waitingReply: 0, // Not displayed for users
            closed: ticketsData.filter((t) => t.status === "closed").length,
          });
        } else {
          // For support/admin: show all statuses separately
          setStats({
            total: ticketsData.length,
            open: ticketsData.filter((t) => t.status === "open").length,
            inProgress: ticketsData.filter((t) => t.status === "in-progress")
              .length,
            waitingReply: ticketsData.filter(
              (t) => t.status === "waiting-reply"
            ).length,
            closed: ticketsData.filter((t) => t.status === "closed").length,
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
      setError("Failed to fetch tickets");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      const response = await api.post("/tickets", {
        ...newTicket,
        language: language,
      });
      if (response.data.success) {
        setTickets([response.data.ticket, ...tickets]);
        const newStats = {
          total: tickets.length + 1,
          open: stats.open + 1, // New tickets are counted as 'open'
          inProgress: stats.inProgress,
          waitingReply: 0, // Not used for users
          closed: stats.closed,
        };
        setStats(newStats);
        setOpenDialog(false);
        setNewTicket({
          title: "",
          description: "",
          priority: "medium",
          category: "general",
        });
      }
    } catch (error) {
      if (error.response?.status === 429) {
        // Rate limit error
        setError(
          error.response?.data?.message ||
            "You have reached the maximum limit of 3 tickets per 24 hours."
        );
      } else {
        setError(error.response?.data?.message || "Failed to create ticket");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTicketClick = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      open: "open",
      "in-progress": "inProgress",
      "waiting-reply": "waitingReply",
      resolved: "resolved",
      closed: "closed",
    };
    return statusMap[status] || status;
  };

  const getCategoryLabel = (category) => {
    const categoryMap = {
      technical: "technical",
      billing: "billing",
      general: "general",
      "feature-request": "featureRequest",
      other: "other",
    };
    return categoryMap[category] || category;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: "warning",
      "in-progress": "info",
      "waiting-reply": "error",
      resolved: "success",
      closed: "default",
    };
    return colors[status] || "default";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "default",
      medium: "info",
      high: "warning",
      urgent: "error",
    };
    return colors[priority] || "default";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        {t("dashboard.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {t("dashboard.welcome").replace("{name}", user?.displayName || "")}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t("dashboard.role")}: <strong>{user?.role}</strong> |{" "}
        {user?.metamaskAddress ? (
          <>
            {t("dashboard.wallet")}: {user.metamaskAddress}
          </>
        ) : (
          <>
            {t("dashboard.email")}: {user?.email}
          </>
        )}
      </Typography>

      {/* Stats - Mobile-friendly compact display */}
      <Box
        sx={{
          mt: 2,
          display: { xs: "block", md: "none" },
        }}
      >
        <Paper sx={{ p: 2 }}>
          {user?.role === "user" ? (
            // User view: Open includes waiting-reply, show "Reply Received" instead of "In Progress"
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{t("dashboard.myTickets")}:</strong>{" "}
                <span style={{ fontSize: "1.2em", color: "#1976d2" }}>
                  {stats.total}
                </span>
                {" | "}
                <strong>{t("dashboard.open")}:</strong>{" "}
                <span style={{ fontSize: "1.2em", color: "#ed6c02" }}>
                  {stats.open}
                </span>
                {" | "}
                <strong>{t("dashboard.replyReceived")}:</strong>{" "}
                <span style={{ fontSize: "1.2em", color: "#0288d1" }}>
                  {stats.inProgress}
                </span>
              </Typography>
              <Typography variant="body1">
                <strong>{t("dashboard.closed")}:</strong>{" "}
                <span style={{ fontSize: "1.2em", color: "#757575" }}>
                  {stats.closed}
                </span>
              </Typography>
            </>
          ) : (
            // Support/Admin view: Show all statuses including waiting-reply
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{t("dashboard.myTickets")}:</strong>{" "}
                <span style={{ fontSize: "1.2em", color: "#1976d2" }}>
                  {stats.total}
                </span>
                {" | "}
                <strong>{t("dashboard.open")}:</strong>{" "}
                <span style={{ fontSize: "1.2em", color: "#ed6c02" }}>
                  {stats.open}
                </span>
                {" | "}
                <strong>{t("dashboard.inProgress")}:</strong>{" "}
                <span style={{ fontSize: "1.2em", color: "#0288d1" }}>
                  {stats.inProgress}
                </span>
              </Typography>
              <Typography variant="body1">
                <strong>{t("status.waitingReply")}:</strong>{" "}
                <span style={{ fontSize: "1.2em", color: "#d32f2f" }}>
                  {stats.waitingReply}
                </span>
                {" | "}
                <strong>{t("dashboard.closed")}:</strong>{" "}
                <span style={{ fontSize: "1.2em", color: "#757575" }}>
                  {stats.closed}
                </span>
              </Typography>
            </>
          )}
        </Paper>
      </Box>

      {/* Stats - Desktop display */}
      <Grid
        container
        spacing={3}
        sx={{ mt: 2, display: { xs: "none", md: "flex" } }}
      >
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="primary">
              {t("dashboard.myTickets")}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1 }}>
              {stats.total}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="warning.main">
              {t("dashboard.open")}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1 }}>
              {stats.open}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="info.main">
              {user?.role === "user"
                ? t("dashboard.replyReceived")
                : t("dashboard.inProgress")}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1 }}>
              {stats.inProgress}
            </Typography>
          </Paper>
        </Grid>
        {user?.role !== "user" && (
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="h6" color="error.main">
                {t("status.waitingReply")}
              </Typography>
              <Typography variant="h4" sx={{ mt: 1 }}>
                {stats.waitingReply}
              </Typography>
            </Paper>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              {t("dashboard.closed")}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1 }}>
              {stats.closed}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 4,
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5">
          {user?.role === "support" || user?.role === "admin"
            ? t("dashboard.supportTickets")
            : t("dashboard.myTickets")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          {t("dashboard.createTicket")}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("ticket.number")}</TableCell>
              <TableCell>{t("ticket.title")}</TableCell>
              <TableCell>{t("ticket.createdBy")}</TableCell>
              <TableCell>{t("ticket.status")}</TableCell>
              <TableCell>{t("ticket.priority")}</TableCell>
              <TableCell>{t("ticket.category")}</TableCell>
              <TableCell>{t("ticket.createdAt")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">
                    {t("dashboard.noTickets")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tickets.map((ticket) => (
                <TableRow
                  key={ticket._id}
                  hover
                  onClick={() => handleTicketClick(ticket._id)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>#{ticket.ticketNumber || "N/A"}</TableCell>
                  <TableCell>
                    {ticket.title.length > 20
                      ? ticket.title.substring(0, 20) + "..."
                      : ticket.title}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                    >
                      {ticket.createdBy?.metamaskAddress ||
                        ticket.createdBy?.email ||
                        "Unknown"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`status.${getStatusLabel(ticket.status)}`)}
                      color={getStatusColor(ticket.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`priority.${ticket.priority}`)}
                      color={getPriorityColor(ticket.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {t(`category.${getCategoryLabel(ticket.category)}`)}
                  </TableCell>
                  <TableCell>
                    {new Date(ticket.createdAt).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("createTicket.title")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("createTicket.ticketTitle")}
            fullWidth
            variant="outlined"
            value={newTicket.title}
            onChange={(e) =>
              setNewTicket({ ...newTicket, title: e.target.value })
            }
            inputProps={{ maxLength: 100 }}
            helperText={`${newTicket.title.length}/100`}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label={t("createTicket.description")}
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={newTicket.description}
            onChange={(e) =>
              setNewTicket({ ...newTicket, description: e.target.value })
            }
            inputProps={{ maxLength: 2000 }}
            helperText={`${newTicket.description.length}/2000`}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label={t("createTicket.priority")}
            fullWidth
            variant="outlined"
            value={newTicket.priority}
            onChange={(e) =>
              setNewTicket({ ...newTicket, priority: e.target.value })
            }
            sx={{ mb: 2 }}
          >
            <MenuItem value="low">{t("priority.low")}</MenuItem>
            <MenuItem value="medium">{t("priority.medium")}</MenuItem>
            <MenuItem value="high">{t("priority.high")}</MenuItem>
            <MenuItem value="urgent">{t("priority.urgent")}</MenuItem>
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("createTicket.category")}
            fullWidth
            variant="outlined"
            value={newTicket.category}
            onChange={(e) =>
              setNewTicket({ ...newTicket, category: e.target.value })
            }
          >
            <MenuItem value="technical">{t("category.technical")}</MenuItem>
            <MenuItem value="billing">{t("category.billing")}</MenuItem>
            <MenuItem value="general">{t("category.general")}</MenuItem>
            <MenuItem value="feature-request">
              {t("category.featureRequest")}
            </MenuItem>
            <MenuItem value="other">{t("category.other")}</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={isSubmitting}>
            {t("createTicket.cancel")}
          </Button>
          <Button
            onClick={handleCreateTicket}
            variant="contained"
            disabled={
              !newTicket.title || !newTicket.description || isSubmitting
            }
          >
            {isSubmitting
              ? t("createTicket.creating")
              : t("createTicket.create")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Dashboard;
